"use client";

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/cn";

const SIZE_CLASS: Record<NonNullable<ModalProps["size"]>, string> = {
  sm: "sm:max-w-[400px]",
  md: "sm:max-w-[560px]",
  lg: "sm:max-w-[720px]",
  xl: "sm:max-w-[920px]",
};

export interface ModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  footer?: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  bodyClassName?: string;
  /** When true, clicking outside the modal does not close it. Useful when a
   *  child renders a portal (camera capture) that Radix would otherwise count
   *  as an outside interaction. */
  blockInteractOutside?: boolean;
  /** Keep the accessible name but hide the header row, for panels whose body
   *  already opens with its own heading. Radix requires a title either way. */
  hideTitle?: boolean;
  children: React.ReactNode;
}

/**
 * Responsive modal: a centered Dialog on `sm+` screens, a bottom Sheet on
 * mobile. Header and footer stay pinned; the BODY is the only scroll region and
 * never scrolls horizontally (prevents the dual-axis scroll a plain
 * `overflow-y-auto` container produced when a child was wider than the box).
 */
export function Modal({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  footer,
  size = "md",
  className,
  bodyClassName,
  blockInteractOutside,
  hideTitle,
  children,
}: ModalProps) {
  const isMobile = useIsMobile();
  // Focus target for the open handler below. currentTarget on Radix's open
  // event is not reliably the panel, so hold a real ref.
  const panelRef = React.useRef<HTMLDivElement>(null);
  const block = blockInteractOutside
    ? {
        onPointerDownOutside: (e: Event) => e.preventDefault(),
        onInteractOutside: (e: Event) => e.preventDefault(),
      }
    : {};

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        {trigger ? <SheetTrigger asChild>{trigger}</SheetTrigger> : null}
        <SheetContent
          // Radix focuses the first focusable child on open. With editable
          // text in the body that is a contentEditable, which pops the iOS
          // keyboard the instant a detail sheet opens. Focus the panel itself
          // instead; an explicit autoFocus on a field still wins.
          ref={panelRef}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            panelRef.current?.focus();
          }}
          tabIndex={-1}
          side="bottom"
          className={cn("flex max-h-[90svh] flex-col gap-0 rounded-t-xl p-0", className)}
          {...block}
        >
          {(title || description) && (
            <SheetHeader className={hideTitle ? "sr-only p-0" : "shrink-0 px-4 pt-4 pb-3 text-left"}>
              {title && <SheetTitle className="text-lg">{title}</SheetTitle>}
              {description && <SheetDescription>{description}</SheetDescription>}
            </SheetHeader>
          )}
          <div className={cn("min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-4", bodyClassName)}>
            {children}
          </div>
          {footer && (
            <div className="shrink-0 flex flex-col-reverse gap-2 border-t border-border px-4 py-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
              {footer}
            </div>
          )}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent
        // Radix focuses the first focusable child on open. With editable
        // text in the body that is a contentEditable, which pops the iOS
        // keyboard the instant a detail sheet opens. Focus the panel itself
        // instead; an explicit autoFocus on a field still wins.
        ref={panelRef}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          panelRef.current?.focus();
        }}
        tabIndex={-1}
        className={cn(
          SIZE_CLASS[size],
          "flex max-h-[85svh] flex-col overflow-x-hidden overflow-y-hidden p-0 gap-0",
          className,
        )}
        {...block}
      >
        {(title || description) && (
          <DialogHeader className={hideTitle ? "sr-only p-0" : "shrink-0 px-6 pt-6 pb-3 text-left"}>
            {title && <DialogTitle>{title}</DialogTitle>}
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
        )}
        <div className={cn("min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-6 py-4", bodyClassName)}>
          {children}
        </div>
        {footer && (
          <div className="shrink-0 flex flex-col-reverse gap-2 px-6 py-3 sm:flex-row sm:justify-end">
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
